<template>
  <div class="chart-log" ref="container">
    <div class="dropdown-group">
      <!-- 차트유형 선택 -->
      <lego-drop-holder-item-icon
        :btnIconType="chartSetings.iconType || 'line'"
        :btnIconName="chartSetings.iconName || 'line_chart'"
        :isDropIcon="true"
        :submenuIcon="true"
        :dropdownData="$const.CHART_TYPE"
        isChartType
        :tooltip="$t('page.performance.graphType')"
        @click="changeChartType"
      >
        <template #buttonTitle>{{ $t('page.performance.graphType') }}</template>
      </lego-drop-holder-item-icon>

      <!-- export유형 선택 -->
      <lego-drop-holder-item-icon
        :btnIconType="'line'"
        :btnIconName="'download_small'"
        :isDropIcon="true"
        :submenuIcon="false"
        :dropdownData="FILE_TYPE"
        :tooltip="$t('page.performance.download')"
        @click="saveFile($event, item)"
        notShowActive
      >
        <template #buttonTitle>{{ $t('page.performance.download') }}</template>
      </lego-drop-holder-item-icon>
    </div>

    <div v-if="checkSeries" class="chart-log__main">
      <ECharts
        ref="chartRef"
        class="cus-echart"
        autoresize
        :option="chartOptions"
        style="height: 100%; width: 100%"
      />
    </div>
    <div v-else class="no-data">
      <lego-icon iconType="picto" icon="widget_nodata" :size="37" color="#d2d3d6" />
      {{ $t('label.noData') }}
    </div>
  </div>
</template>

<script setup>
import { defineProps, onMounted, reactive, ref, computed, getCurrentInstance } from 'vue';
import ECharts from 'vue-echarts';
import 'echarts';
import html2canvas from 'html2canvas';
import i18n from '@/i18n';
import {
  getFormatSearchPreiod,
  customStyleHeader
} from '@/containers/performance/productMetricLib.js';
import moment from 'moment';
import { useChart } from '@/composables/useChart.js';
import { useDateTimeFormat } from '@/composables/useDateTimeFormat';

const { formatDateTime, FULL_TIME_FORMAT } = useDateTimeFormat();

//props
const props = defineProps({
  opt: { type: Object },
  dateOption: { type: Object }
});

const { setChartGraphic, resetChartGraphic } = useChart();
// ref
const { $modal } = getCurrentInstance().appContext.config.globalProperties;

const chartRef = ref(null);
const lastLegend = ref('');
const container = ref();

const FILE_TYPE = [
  { txt: 'Chart (PNG)', type: 'image' },
  { txt: 'Raw Data (Excel)', type: 'excel-raw' }
];

const chartSetings = reactive({
  iconType: 'line',
  iconName: 'line_chart',
  chartType: 'line',
  chartExopt: {}
});

const changeChartType = (selItem) => {
  chartSetings.chartExopt = selItem.chartExopt;
  chartSetings.chartType = selItem.chartType;
  chartSetings.iconType = selItem.iconType;
  chartSetings.iconName = selItem.iconName;
  lastLegend.value = '';
};

const $T = (text) => {
  return i18n.global.t(text);
};

const chartOptions = computed(() => {
  const exopt = chartSetings.chartExopt || {};
  exopt['stack'] = exopt['areaStyle'] ? 'total' : ''; //area style 이 null이 아닌경우에는 stacked로 판단 - jwooni
  exopt['areaStyle'] = exopt['areaStyle'] || null;
  exopt['symbol'] = exopt['symbol'] || 'circle';
  exopt['symbolSize'] = exopt['symbolSize'] || 3;
  const listItem = props.opt.series;
  const series = listItem.map((item) => ({
    ...item,
    ...exopt,
    type: chartSetings.chartType
  }));
  const newOpt = { ...props.opt };
  const formatter = (params) => {
    let label = params.value;
    if (params.axisDimension == 'x') {
      label = formatDateTime(label, FULL_TIME_FORMAT);
    }
    return label;
  };
  newOpt.series = series;
  newOpt.tooltip = { ...props.opt.tooltip, axisPointer: { label: { formatter } } };
  return newOpt;
});

const checkSeries = computed(() => {
  const listItem = props.opt.series;
  const series = listItem.map((item) => item.data).flat();
  return !!series.length;
});
const saveFile = (selItem) => {
  if (!checkSeries.value) {
    $modal.open({
      rootId: 'app',
      type: 'alert',
      title: $T('title.notice'),
      message: $T('message.noDownloadData'),

      action: () => {}
    });
  } else {
    $modal.open({
      rootId: 'app',
      type: 'confirm',
      title: $T('title.notice'),
      message: `<div>` + $T('page.performance.downloadCheck') + `</div>`,
      action: () => {
        if (selItem.type === 'image') {
          const chartRef = container.value;
          const dropdownGroup = chartRef.querySelector('.dropdown-group');
          dropdownGroup.style.display = 'none';
          downloadMetricDataImage(chartRef);
          dropdownGroup.style.display = 'flex';
        } else if (selItem.type === 'excel-raw') {
          downloadMetricDataExcel();
        }
      },
      cancel: () => {}
    });
  }
};

const generateDownloadFileName = () => {
  const series = props.opt.series[0];
  let fileName = (lastLegend.value || series.name) + '_';
  fileName += formatDateTime().replace(/[-: "]/g, '');
  return fileName;
};

const downloadMetricDataImage = (chartElement) => {
  html2canvas(chartElement).then((canvas) => {
    const link = document.createElement('a');

    let fileName = generateDownloadFileName();
    link.setAttribute('download', fileName + '.png');
    link.setAttribute(
      'href',
      canvas.toDataURL('image/png').replace('image/png', 'image/octet-stream')
    );
    link.click();
  });
};

const downloadMetricDataExcel = () => {
  const xlsx = require('xlsx');
  const startDt = props.dateOption.queryStartDt;
  const endDt = props.dateOption.queryEndDt;

  const dataPeriod = getFormatSearchPreiod(startDt, endDt);

  let excelDatas = [];
  excelDatas.push([dataPeriod]);
  excelDatas.push([]);
  excelDatas.push([
    $T('excel.ts'),
    $T('excel.perfValue'),
    $T('excel.productName'),
    $T('excel.unit')
  ]);

  const _unit = 'cnt';
  const series = props.opt.series;
  series.forEach((raw) => {
    const _productName = raw.name;

    raw.downloadData.forEach((rawSub) => {
      const ts = formatDateTime(rawSub[0], FULL_TIME_FORMAT);
      let value = rawSub[1];

      let row = [ts, value, _productName, _unit];
      excelDatas.push(row);
    });
  });

  const workSheet = xlsx.utils.aoa_to_sheet(excelDatas);

  // chell style
  workSheet['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 4 } }];
  workSheet['!cols'] = [{ wch: 20 }, { wch: 20 }, { wch: 50 }, { wch: 5 }];
  workSheet['!rows'] = [{ hpx: 40 }];

  // A1
  workSheet['A1'].s = {
    alignment: {
      vertical: 'center',
      horizontal: 'center'
    },
    font: {
      sz: 15,
      bold: true,
      color: { rgb: 'FFFF0000' }
    }
  };

  customStyleHeader(workSheet, 4, 10);
  const workBook = xlsx.utils.book_new();
  xlsx.utils.book_append_sheet(workBook, workSheet, 'perf_data');
  const fileName = generateDownloadFileName() + '.xlsx';
  xlsx.writeFile(workBook, fileName);
};

onMounted(() => {
  window.setTimeout(() => {
    if (chartRef.value) {
      chartRef.value.chart.on('legendselectchanged', (params) => {
        chartRef.value.chart.dispatchAction({
          type: 'legendAllSelect'
        });
        if (Object.keys(params.selected).length > 1) {
          resetChartGraphic(chartRef.value);
        }
        if (lastLegend.value !== params.name) {
          chartRef.value.dispatchAction({
            type: 'legendUnSelect',
            name: params.name
          });
          lastLegend.value = params.name;
          chartRef.value.dispatchAction({
            type: 'legendInverseSelect'
          });
          setChartGraphic(params, chartRef.value);
        } else {
          setChartGraphic(null, chartRef.value, true);
          lastLegend.value = '';
        }
      });
    }
  }, 100);
});
</script>

<style scoped lang="scss">
.dropdown-group {
  height: 20px;
  gap: 16px;
}

.chart-log {
  width: 100%;
  height: 100%;
  padding: 16px;
  display: flex;
  flex-direction: column;
}

.chart-log__main {
  width: 100%;
  height: 178px;
}

.no-data {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1 1 auto;
  color: rgba(51, 51, 51, 0.4);
  font-weight: 500;
  font-size: 14px;
  gap: 12px;
}
</style>
